import { useLinkTo, useNavigation } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { BackGroundImageView } from 'screens/Login/styles';
import { isMobileOrSmallScreen } from 'utils/deviceInfo';
import { calcSizeForDevice, customSmallNumbersString, verifyIsSameDay } from 'utils/index';

import NewGradientButton from 'components/Button/NewGradienteButton';
import { usePlayer } from 'components/Player/context';

export interface TransmitionCatchProps {
  endTime?: string;
  startTime?: string;
}

const wrongTimeImageLarge = 'https://img.band.com.br/image/2025/03/25/bgtransmissaodesk-105322.png';
const transmissionWrongTimeMobile =
  'https://img.band.com.br/image/2025/03/25/bgtransmissaomobile-105415.png';
const backgroundTimeEndLarge =
  'https://img.band.com.br/image/2025/03/25/bgfimtransmissaodesk-105531.png';
const backgroundTimeEndMobile =
  'https://img.band.com.br/image/2025/03/25/bgfimtransmissaomobile-161522.png';

const TransmitionCatch: React.FC<TransmitionCatchProps> = (props) => {
  const linkTo = useLinkTo();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    title: '',
    description: '',
    image: '',
  });
  const { width } = useWindowDimensions();
  const { TransmissionScreen } = useGlobalConfig();
  const navigation = useNavigation();
  const { setHasTransmissionError } = usePlayer();

  useEffect(() => {
    return navigation.addListener('beforeRemove', async () => {
      setHasTransmissionError(false);
    });
  }, []);

  useEffect(() => {
    setHasTransmissionError(true);
  }, []);

  useEffect(() => {
    if (props?.endTime) {
      setData({
        title: 'A TRANSMISSÃO\nFOI ENCERRADA',
        description: 'Agradecemos por assistir! Continue conectado para futuras transmissões.',
        image: isMobileOrSmallScreen(width)
          ? TransmissionScreen?.endedTransmissionImage_mobile.url ?? backgroundTimeEndMobile
          : TransmissionScreen?.endedTransmissionImage_large.url ?? backgroundTimeEndLarge,
      });
      setIsLoading(false);
    } else if (props?.startTime) {
      const isCurrentDay = verifyIsSameDay(new Date(), new Date(props?.startTime));
      const dueDate = new Date(props?.startTime);

      const hours = dueDate.getHours();
      const customHours = customSmallNumbersString(hours.toString().length, hours);

      const minutes = dueDate.getMinutes();
      const customMinutes = customSmallNumbersString(minutes.toString().length, minutes);

      const day = dueDate.getDate();
      const customDay = customSmallNumbersString(day.toString().length, day);

      const startTime = !isCurrentDay
        ? `NO DIA ${customDay} ÀS ${customHours}H${customMinutes}`
        : `ÀS ${customHours}H${customMinutes}`;

      setData({
        title: `ESSE CONTEÚDO VAI ESTAR${
          isMobileOrSmallScreen(width) ? ' ' : '\n'
        }DISPONÍVEL ${startTime}`,
        description: `Que tal ver outra coisa até lá?\nVocê está no controle! Clique aqui para${
          isMobileOrSmallScreen(width) ? ' ' : '\n'
        }escolher um dos nossos conteúdos:`,
        image: isMobileOrSmallScreen(width)
          ? TransmissionScreen?.wrongTimeImage_mobile.url ?? transmissionWrongTimeMobile
          : TransmissionScreen?.wrongTimeImage_large.url ?? wrongTimeImageLarge,
      });
      setIsLoading(false);
    }
  }, [props]);

  return (
    <View style={styles.container}>
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#ffffff" />
        </View>
      ) : (
        <BackGroundImageView source={{ uri: data.image }}>
          <View style={styles.contentBox}>
            <View
              style={[
                styles.innerView,
                { paddingHorizontal: calcSizeForDevice(isMobileOrSmallScreen(width) ? 5 : 16) },
              ]}>
              <Text
                style={[
                  styles.titleText,
                  {
                    fontSize: calcSizeForDevice(isMobileOrSmallScreen(width) ? 32 : 56, 100),
                    lineHeight: calcSizeForDevice(isMobileOrSmallScreen(width) ? 44 : 68, 126),
                  },
                ]}>
                {data.title}
              </Text>

              <Text
                style={[
                  styles.descriptionText,
                  {
                    fontSize: calcSizeForDevice(isMobileOrSmallScreen(width) ? 18 : 24, 34),
                    lineHeight: calcSizeForDevice(isMobileOrSmallScreen(width) ? 28 : 32, 42),
                    width: isMobileOrSmallScreen(width) ? 'auto' : '65%',
                    marginTop: calcSizeForDevice(isMobileOrSmallScreen(width) ? 28 : 48),
                    marginBottom: calcSizeForDevice(isMobileOrSmallScreen(width) ? 47 : 32),
                  },
                ]}>
                {data.description}
              </Text>
              <View
                style={[
                  styles.buttonContainer,
                  { marginHorizontal: width > 500 ? undefined : 'auto' },
                ]}>
                <NewGradientButton
                  useTVFocus
                  hasTVPreferredFocus
                  style={{ height: 54, width: '100%' }}
                  onPress={() => linkTo('/')}>
                  <Text style={styles.buttonText}>Voltar para página inicial</Text>
                </NewGradientButton>
              </View>
            </View>
          </View>
        </BackGroundImageView>
      )}
    </View>
  );
};

export default TransmitionCatch;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#161233',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentBox: {
    padding: 16,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  innerView: {
    padding: 16,
    alignItems: 'center',
  },
  titleText: {
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    textShadowColor: '#00000077',
    textShadowOffset: { width: 0, height: 3 },
    textShadowRadius: 10,
  },
  descriptionText: {
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    textShadowColor: '#00000077',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 10,
  },
  buttonContainer: {
    width: 300,
  },
  buttonText: {
    fontWeight: 'bold',
    color: '#070d31',
    fontSize: 14,
  },
});
